type IsRouteActiveOptions = {
  exact?: boolean;
};

export function normalizeHref(href: string): string {
  return href.split("?")[0].replace(/\/$/, "");
}

function hrefMatchesPath(
  normalizedPath: string,
  normalizedHref: string,
  exact: boolean,
): boolean {
  if (exact) {
    return normalizedPath === normalizedHref;
  }
  return (
    normalizedPath === normalizedHref ||
    normalizedPath.startsWith(normalizedHref + "/")
  );
}

function isExactHref(normalizedHref: string, exact?: boolean): boolean {
  return (
    Boolean(exact) ||
    normalizedHref === "/dashboard" ||
    normalizedHref === "/admin"
  );
}

const RECORD_FILTER_PARAM = "f";
const FILTER_CONDITION_SEPARATOR = "~";

function paramSatisfied(
  key: string,
  wantedValue: string,
  current: URLSearchParams,
): boolean {
  const currentValue = current.get(key);
  if (currentValue === null) return false;
  if (key !== RECORD_FILTER_PARAM) return currentValue === wantedValue;
  const active = new Set(currentValue.split(FILTER_CONDITION_SEPARATOR));
  return wantedValue
    .split(FILTER_CONDITION_SEPARATOR)
    .every((condition) => active.has(condition));
}

export function isRouteActive(
  pathname: string,
  href: string,
  options?: IsRouteActiveOptions,
): boolean {
  const normalizedPath = pathname.replace(/\/$/, "");
  const normalizedHref = normalizeHref(href);
  return hrefMatchesPath(
    normalizedPath,
    normalizedHref,
    isExactHref(normalizedHref, options?.exact),
  );
}

export function resolveActiveHref(
  pathname: string,
  hrefs: Array<{ href: string; exact?: boolean }>,
  search?: string,
): string | null {
  const normalizedPath = pathname.replace(/\/$/, "");
  const current = new URLSearchParams(search ?? "");
  let best: string | null = null;
  let bestScore = -1;

  for (const { href, exact } of hrefs) {
    const normalizedHref = normalizeHref(href);
    if (
      !hrefMatchesPath(
        normalizedPath,
        normalizedHref,
        isExactHref(normalizedHref, exact),
      )
    ) {
      continue;
    }

    const [, query] = href.split("?");
    if (query) {
      const wanted = new URLSearchParams(query);
      const satisfied = [...wanted].every(([key, value]) =>
        paramSatisfied(key, value, current),
      );
      if (!satisfied) continue;
    }

    const score = normalizedHref.length * 2 + (query ? 1 : 0);
    if (score > bestScore) {
      best = href;
      bestScore = score;
    }
  }

  return best;
}

export function isTabActive(
  pathname: string,
  tabHref: string,
  basePath: string,
  knownTabSlugs: Array<string> = [],
): boolean {
  const normalizedPath = pathname.replace(/\/$/, "");
  const normalizedBasePath = basePath.replace(/\/$/, "");
  const normalizedTabHref = tabHref.replace(/\/$/, "");

  const relativePath = normalizedPath
    .replace(normalizedBasePath, "")
    .replace(/^\//, "");
  const tabSlug = normalizedTabHref
    .replace(normalizedBasePath, "")
    .replace(/^\//, "");

  if (!tabSlug) {
    if (relativePath === "") {
      return true;
    }
    const firstSegment = relativePath.split("/")[0];
    const isKnownTab = knownTabSlugs.some(
      (slug) =>
        slug && (firstSegment === slug || relativePath.startsWith(slug + "/")),
    );
    return !isKnownTab;
  }

  return relativePath === tabSlug || relativePath.startsWith(tabSlug + "/");
}
